/**
 * OpenAI API Helper Utility
 *
 * Provides centralized API calling functionality with:
 * - API key validation (Issue #4)
 * - Timeout handling with AbortController (Issue #5)
 * - Retry logic with exponential backoff (Issue #6)
 * - Response validation
 * - Consistent error handling (Issue #7)
 *
 * Based on best practices from MermaidDiagram.tsx
 */

import { createAppError, logError as logAppError } from './errorHandler';

// API Configuration
export const API_CONFIG = {
  TIMEOUT: 180000, // 3 minutes
  MAX_RETRIES: 3, // Maximum retry attempts
  BASE_URL: 'https://api.openai.com/v1/chat/completions',
} as const;

// Custom error type for API errors
export interface ApiError extends Error {
  status?: number;
  code?: string;
}

/**
 * Creates a custom API error with status code
 */
export const createApiError = (message: string, status?: number, code?: string): ApiError => {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.code = code;
  return error;
};

/**
 * Creates an abort error for user-initiated cancellation.
 *
 * A plain Error carrying name 'AbortError' rather than fetch's DOMException,
 * which is not reliably `instanceof Error` in every Office webview — callers
 * detect cancellation with requestManager.isAborted() and must not see it as
 * a timeout.
 */
const createAbortError = (): Error => {
  const error = new Error('Request aborted by user');
  error.name = 'AbortError';
  return error;
};

/**
 * Returns the single AI model configured for the app.
 */
export const getConfiguredAIModel = (): string => {
  const model = import.meta.env.VITE_AI_MODEL?.trim();
  if (!model) {
    throw createApiError(
      'AI model is not configured. Please set VITE_AI_MODEL in your environment settings.',
      500,
      'MISSING_AI_MODEL'
    );
  }
  return model;
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Validates the OpenAI API key
 * Issue #4: Missing API Key Validation
 */
export const validateApiKey = (apiKey?: string): { valid: boolean; error?: string } => {
  if (!apiKey || apiKey.trim().length === 0) {
    return {
      valid: false,
      error: 'OpenAI API key is not configured. Please check your environment settings.',
    };
  }

  // Basic format validation for OpenAI API keys
  if (!apiKey.startsWith('sk-')) {
    return {
      valid: false,
      error: 'Invalid API key format. OpenAI API keys should start with "sk-".',
    };
  }

  return { valid: true };
};

/**
 * OpenAI API Request Options
 */
export interface OpenAIRequestOptions {
  messages: Array<{
    role: 'system' | 'user' | 'assistant';
    content: string;
  }>;
  temperature?: number;
  response_format?: { type: 'json_object' | 'text' };
  max_tokens?: number;
  signal?: AbortSignal; // Issue #12: Optional external abort signal
  /** Called before each retry attempt so the UI can show progress. */
  onRetry?: (attempt: number, maxRetries: number) => void;
}

/**
 * OpenAI API Response
 */
export interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * Calls the OpenAI API with timeout, retry logic, and validation
 *
 * Issues Fixed:
 * - #4: API key validation
 * - #5: Timeout handling (60 seconds)
 * - #6: Retry logic with exponential backoff
 * - #12: Support for external abort signal
 *
 * @param options - OpenAI API request options
 * @param attempt - Current retry attempt (internal use)
 * @returns OpenAI API response
 * @throws ApiError with specific error messages
 */
export const callOpenAI = async (
  options: OpenAIRequestOptions,
  attempt = 0
): Promise<OpenAIResponse> => {
  // Check for Azure configuration
  const AZURE_API_KEY = import.meta.env.VITE_AZURE_API_KEY;
  const AZURE_API_BASE = import.meta.env.VITE_AZURE_API_BASE;
  const AZURE_API_VERSION = import.meta.env.VITE_AZURE_API_VERSION;

  // Check for standard OpenAI configuration
  const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
  const configuredModel = getConfiguredAIModel();

  // Determine which service to use
  const useAzure = !!(AZURE_API_KEY && AZURE_API_BASE);

  let apiUrl: string = API_CONFIG.BASE_URL;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (useAzure) {
    // Azure OpenAI Endpoint format: https://{your-resource-name}.openai.azure.com/openai/deployments/{deployment-id}/chat/completions?api-version={api-version}
    // Remove trailing slash if present
    const baseUrl = AZURE_API_BASE?.replace(/\/$/, '');
    apiUrl = `${baseUrl}/openai/deployments/${configuredModel}/chat/completions?api-version=${AZURE_API_VERSION}`;
    headers['api-key'] = AZURE_API_KEY;
  } else {
    // Standard OpenAI
    const validation = validateApiKey(OPENAI_API_KEY);
    if (!validation.valid) {
      throw createApiError(validation.error || 'Invalid API key', 401, 'INVALID_API_KEY');
    }
    headers['Authorization'] = `Bearer ${OPENAI_API_KEY}`;
  }

  // Set default values
  let temperature = options.temperature ?? 0.7;

  // Azure deployments for this app require temperature 1.
  if (useAzure) {
    temperature = 1;
  }

  // BUG-05 fix: Destructure signal and temperature out before spreading
  // so the Azure temperature override is never silently defeated,
  // and `signal` is not serialized into the JSON request body.
  const { signal: externalSignal, temperature: _callerTemp, ...restOptions } = options;

  const requestOptions = {
    ...restOptions,
    model: configuredModel,
    temperature: temperature, // Applied last — Azure override always wins
  };

  // Issue #5 & #12: Create abort controller for timeout handling
  // If external signal provided, listen to it as well
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  // Issue #12: Listen to external abort signal if provided
  // BUG-08 fix: Use { once: true } to prevent listener accumulation across retries
  if (externalSignal) {
    externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestOptions),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Issue #6: Retry logic for rate limits (429) and server errors (500, 502, 503, 504)
    if (!response.ok) {
      const shouldRetry =
        (response.status === 429 || response.status >= 500) &&
        attempt < API_CONFIG.MAX_RETRIES;

      if (shouldRetry) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`[OpenAI] Retrying... Attempt ${attempt + 1}/${API_CONFIG.MAX_RETRIES} (${response.status})`);
        options.onRetry?.(attempt + 1, API_CONFIG.MAX_RETRIES);
        await sleep(delay);
        return callOpenAI(options, attempt + 1);
      }

      // Parse error response
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.error?.message || `API request failed: ${response.statusText}`;

      throw createApiError(errorMessage, response.status, errorData?.error?.code);
    }

    const data = await response.json();

    // Validate API response structure
    if (!data.choices || !Array.isArray(data.choices) || data.choices.length === 0) {
      throw createApiError('Invalid API response structure: missing choices array', 500, 'INVALID_RESPONSE');
    }

    if (!data.choices[0].message || !data.choices[0].message.content) {
      throw createApiError('Invalid API response structure: missing message content', 500, 'INVALID_RESPONSE');
    }

    return data as OpenAIResponse;

  } catch (fetchError) {
    clearTimeout(timeoutId);

    // Handle aborts: a user cancellation must stay recognisable as an abort,
    // only a real timeout becomes a 408 the UI shows as an error.
    if (fetchError instanceof Error && fetchError.name === 'AbortError') {
      if (externalSignal?.aborted) throw createAbortError();
      throw createApiError(
        'The AI took too long to respond. Try selecting a shorter passage, or check your internet connection and try again.',
        408,
        'TIMEOUT'
      );
    }

    // Handle network errors
    if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
      // Issue #6: Retry network errors
      if (attempt < API_CONFIG.MAX_RETRIES) {
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`[OpenAI] Network error, retrying... Attempt ${attempt + 1}/${API_CONFIG.MAX_RETRIES}`);
        options.onRetry?.(attempt + 1, API_CONFIG.MAX_RETRIES);
        await sleep(delay);
        return callOpenAI(options, attempt + 1);
      }

      throw createApiError(
        'Network error. Please check your internet connection and try again.',
        0,
        'NETWORK_ERROR'
      );
    }

    // Re-throw API errors
    throw fetchError;
  }
};

/**
 * Helper function to get user-friendly error messages
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const apiError = error as ApiError;

    // Specific error messages based on status code
    switch (apiError.status) {
      case 401:
        return 'API authentication failed. Please check your OpenAI API key configuration.';
      case 403:
        return 'Access forbidden. Your API key may not have permission for this operation.';
      case 404:
        return 'API endpoint not found. Please contact support.';
      case 408:
        return 'Request timed out. Please try again with a smaller selection.';
      case 429:
        return 'Rate limit exceeded. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'OpenAI service error. Please try again later.';
      default:
        return apiError.message || 'An unexpected error occurred. Please try again.';
    }
  }

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Helper function to extract content from OpenAI response
 */
export const extractContent = (response: OpenAIResponse): string => {
  return response.choices[0].message.content.trim();
};

/**
 * Helper function to parse JSON response from OpenAI
 * Handles cases where the response might be wrapped in markdown code blocks
 */
export const parseJsonResponse = <T = unknown>(response: OpenAIResponse): T => {
  const content = extractContent(response);

  // Remove markdown code blocks if present
  const jsonMatch = content.match(/```json\n?([\s\S]*?)```/);
  const jsonString = jsonMatch ? jsonMatch[1].trim() : content;

  try {
    return JSON.parse(jsonString) as T;
  } catch {
    throw createApiError(
      'Failed to parse JSON response from OpenAI. The response may not be valid JSON.',
      500,
      'JSON_PARSE_ERROR'
    );
  }
};

/**
 * Logs errors consistently across features
 * Issue #7: Uses centralized error handling
 */
export const logError = (context: string, error: unknown, metadata?: Record<string, unknown>): void => {
  const appError = createAppError(error, context, metadata);
  logAppError(appError);
};

/** Internal: performs a single streaming fetch attempt. */
async function* _streamOnce(
  apiUrl: string,
  headers: Record<string, string>,
  body: string,
  signal: AbortSignal,
): AsyncGenerator<string, void, undefined> {
  const response = await fetch(apiUrl, { method: 'POST', headers, body, signal });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw createApiError(
      `API error (${response.status}): ${errorText.substring(0, 200)}`,
      response.status,
      'API_ERROR'
    );
  }

  if (!response.body) {
    throw createApiError('No response body for streaming', 500, 'NO_STREAM_BODY');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.startsWith('data: ')) continue;
      const data = trimmed.slice(6);
      if (data === '[DONE]') return;

      try {
        const parsed = JSON.parse(data);
        const delta = parsed.choices?.[0]?.delta?.content;
        if (delta) yield delta;
      } catch {
        // Skip malformed SSE lines
      }
    }
  }
}

/**
 * F-04: Streaming version of callOpenAI
 * Uses SSE (Server-Sent Events) with stream: true.
 * Yields content delta chunks as they arrive.
 * Retries on 429 / 5xx errors, calling options.onRetry before each retry.
 */
export async function* callOpenAIStream(
  options: OpenAIRequestOptions
): AsyncGenerator<string, void, undefined> {
  const AZURE_API_KEY = import.meta.env.VITE_AZURE_API_KEY;
  const AZURE_API_BASE = import.meta.env.VITE_AZURE_API_BASE;
  const AZURE_API_VERSION = import.meta.env.VITE_AZURE_API_VERSION;
  const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
  const configuredModel = getConfiguredAIModel();

  const useAzure = !!(AZURE_API_KEY && AZURE_API_BASE);

  let apiUrl: string = API_CONFIG.BASE_URL;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (useAzure) {
    const baseUrl = AZURE_API_BASE?.replace(/\/$/, '');
    apiUrl = `${baseUrl}/openai/deployments/${configuredModel}/chat/completions?api-version=${AZURE_API_VERSION}`;
    headers['api-key'] = AZURE_API_KEY;
  } else {
    const validation = validateApiKey(OPENAI_API_KEY);
    if (!validation.valid) {
      throw createApiError(validation.error || 'Invalid API key', 401, 'INVALID_API_KEY');
    }
    headers['Authorization'] = `Bearer ${OPENAI_API_KEY}`;
  }

  let temperature = options.temperature ?? 0.7;
  if (useAzure) temperature = 1;

  const { signal: externalSignal, temperature: _callerTemp, onRetry, ...restOptions } = options;

  const requestOptions = { ...restOptions, model: configuredModel, temperature, stream: true };
  const body = JSON.stringify(requestOptions);

  let attempt = 0;
  while (true) {
    // Fresh controller and timeout per attempt: reusing one leaves every retry
    // running with a cleared timeout, so a stalled stream would hang forever.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
    const forwardAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', forwardAbort, { once: true });
    }

    try {
      yield* _streamOnce(apiUrl, headers, body, controller.signal);
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        if (externalSignal?.aborted) throw createAbortError();
        throw createApiError(
          'The AI took too long to respond. Try selecting a shorter passage, or check your internet connection and try again.',
          408,
          'TIMEOUT'
        );
      }

      const apiErr = err as ApiError;
      const isRetryable = apiErr.status === 429 || (apiErr.status !== undefined && apiErr.status >= 500);
      if (!isRetryable || attempt >= API_CONFIG.MAX_RETRIES) throw err;

      attempt++;
      console.log(`[OpenAI Stream] Retrying... Attempt ${attempt}/${API_CONFIG.MAX_RETRIES} (${apiErr.status})`);
      onRetry?.(attempt, API_CONFIG.MAX_RETRIES);
      await sleep(Math.pow(2, attempt - 1) * 1000);
    } finally {
      // Also runs when the consumer abandons the generator mid-stream.
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', forwardAbort);
    }
  }
}
